import { Icon } from "@mdi/react";
import { FC, useEffect, useState } from "react";

import { mdiAlertCircle } from "@mdi/js";
import { Input, Modal } from "antd";

import { StoredFeatureTemplate } from "../../api/featureTemplate";
import { DEFAULT_TEMPLATE_NAME } from "./defaultTemplate";
import { MlFeature } from "./types";

interface SaveTemplateModalProps {
  open: boolean;
  onClose: () => void;
  featureList: MlFeature[];
  templates: StoredFeatureTemplate[];
  onTemplateSaved: (savedTemplateName: string) => void;
}

const SaveTemplateModal: FC<SaveTemplateModalProps> = ({
  open,
  onClose,
  templates,
  onTemplateSaved
}) => {
  const [templateName, setTemplateName] = useState("");
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (open) {
      setTemplateName("");
      setNameError("");
    }
  }, [open]);

  const checkNameValidity = (name: string): string => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return "Template name cannot be empty";
    }

    if (DEFAULT_TEMPLATE_NAME.toLowerCase() === trimmedName.toLowerCase()) {
      return `Cannot use the reserved name "${DEFAULT_TEMPLATE_NAME}"`;
    }

    const validTemplates = templates.filter((t) => t.name && typeof t.name === "string");
    const existingTemplate = validTemplates.find(
      (t) => t.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingTemplate) {
      return `A template named "${trimmedName}" already exists`;
    }

    return "";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemplateName(value);
    const error = checkNameValidity(value);
    setNameError(error);
  };

  const handleSave = () => {
    const error = checkNameValidity(templateName);
    if (error) {
      setNameError(error);
      return;
    }

    const trimmedName = templateName.trim();
    onTemplateSaved(trimmedName);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!nameError) {
        handleSave();
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Save Template"
      okText="Save"
      cancelText="Cancel"
      onOk={handleSave}
      okButtonProps={{ disabled: !!nameError || !templateName.trim() }}>
      <div>
        <p style={{ marginBottom: 8 }}>Enter a name for this template:</p>
        <Input
          placeholder="e.g., Production Features"
          value={templateName}
          status={nameError ? "error" : ""}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          autoFocus
        />
        {nameError && (
          <div
            style={{
              marginTop: 8,
              color: "#ff4d4f",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
            <Icon path={mdiAlertCircle} size={"16px"} />
            <span>{nameError}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SaveTemplateModal;
